import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { ScrollArea } from "./ui/scroll-area";
import {
  FileText,
  Check,
  Loader2,
  Shield,
  Clock,
  CheckCircle2,
  ArrowLeft,
  Eye,
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import useDealDocuments from "../hooks/useDealDocuments";

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DocumentChecklistModal = ({
  open,
  onOpenChange,
  deal,
  onUpdate,
  onDocumentSigned,
}) => {
  const [signingDoc, setSigningDoc] = useState(null);
  const [documentsState, setDocumentsState] = useState({});
  const [viewStep, setViewStep] = useState(null); // null | { doc, step: "view" | "sign" }
  const [agreed, setAgreed] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [nameError, setNameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const { user } = useAuth();
  const { documents: dealDocuments, loadingDocs } = useDealDocuments();

  const handleViewDocument = async (docKey, dealId) => {
    setViewingDoc(docKey);
    try {
      const response = await axios.get(`${API}/deals/documents/presigned-url`, {
        params: { key: docKey, deal_id: dealId, download: false },
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      const url = response.data?.url;
      if (!url) throw new Error("No URL returned");
      window.open(url, "_blank");
    } catch {
      toast.error("Failed to load document");
    } finally {
      setViewingDoc(null);
    }
  };

  useEffect(() => {
    if (deal?.documents_signed) {
      setDocumentsState(deal.documents_signed);
    }
  }, [deal]);

  const signedCount = Object.values(documentsState).filter(
    (v) => v !== null,
  ).length;
  const progress =
    dealDocuments.length > 0 ? (signedCount / dealDocuments.length) * 100 : 0;
  const allComplete =
    dealDocuments.length > 0 && signedCount === dealDocuments.length;

  const validateLegalName = (value) => {
    const nextValue = value.trim();

    if (!user?.name) {
      setNameError("Your account name is not available yet.");
      return false;
    }

    if (!nextValue) {
      setNameError("Enter your legal name to continue.");
      return false;
    }

    if (nextValue !== user.name) {
      setNameError("Name must exactly match your legal name");
      return false;
    }

    setNameError("");
    return true;
  };

  const handleStartSign = (doc) => {
    setViewStep({ doc, step: "view" });
    setAgreed(false);
    setLegalName("");
    setNameError("");
  };

  const handleConfirmSign = async () => {
    if (!deal || !viewStep || !agreed) return;
    if (!validateLegalName(legalName)) return;

    const docKey = viewStep.doc.key;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/deals/${deal.deal_id}/sign-document`,
        {
          deal_id: deal.deal_id,
          document_type: docKey,
          signed_legal_name: legalName.trim(),
        },
        { withCredentials: true, headers: getAuthHeaders() },
      );

      setDocumentsState(response.data.documents_signed);
      toast.success(`${viewStep.doc.label} signed successfully!`);
      setViewStep(null);
      setAgreed(false);
      setLegalName("");
      setNameError("");

      if (onUpdate) onUpdate(response.data);
      if (onDocumentSigned) onDocumentSigned(response.data);

      if (response.data.all_documents_complete) {
        toast.success("All documents signed! Deal is now in closing.");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to sign document";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!deal) return null;

  if (loadingDocs) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogTitle className="sr-only">Loading documents</DialogTitle>
          <DialogDescription className="sr-only">
            Loading document list.
          </DialogDescription>
          <div className="py-12 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading documents...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Sub-view: View Document Content
  if (viewStep?.step === "view") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto"
          data-testid="document-view-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {viewStep.doc.label}
            </DialogTitle>
            <DialogDescription>{viewStep.doc.description}</DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[400px] border rounded-lg p-4 bg-muted/30">
            <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">
              {viewStep.doc.content}
            </pre>
          </ScrollArea>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setViewStep(null)}
              className="flex-1"
              data-testid="doc-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Checklist
            </Button>
            <Button
              onClick={() => setViewStep({ ...viewStep, step: "sign" })}
              className="flex-1 bg-blue-500 hover:bg-blue-600"
              data-testid="doc-proceed-sign-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              Proceed to Sign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Sub-view: Sign Document
  if (viewStep?.step === "sign") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[550px]"
          data-testid="document-sign-modal"
        >
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
            <DialogTitle className="text-center text-xl font-heading">
              Sign {viewStep.doc.label}
            </DialogTitle>
            <DialogDescription className="text-center">
              Review and accept the terms to sign this document
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="font-medium text-foreground">
                {deal.property_address}
              </p>
              <p className="text-sm text-muted-foreground">
                {deal.city}, {deal.state} {deal.zip_code}
              </p>
            </div>

            <div className="flex items-start space-x-3 p-4 border rounded-lg bg-card">
              <Checkbox
                id={`agree-${viewStep.doc.key}`}
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-1"
                data-testid="doc-agree-checkbox"
              />
              <Label
                htmlFor={`agree-${viewStep.doc.key}`}
                className="text-sm leading-relaxed cursor-pointer"
              >
                I have read, understood, and agree to be bound by all terms of
                the {viewStep.doc.label}. I understand that my electronic
                signature has the same legal effect as a handwritten signature.
              </Label>
            </div>

            <div className="space-y-2 p-4 border rounded-lg bg-card">
              <Label
                htmlFor="signed-legal-name"
                className="text-sm font-medium"
              >
                Enter your legal name
              </Label>
              <Input
                id="signed-legal-name"
                value={legalName}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setLegalName(nextValue);
                  validateLegalName(nextValue);
                }}
                placeholder={user?.name || "Your full legal name"}
                data-testid="doc-signed-legal-name"
              />

              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setViewStep({ ...viewStep, step: "view" })}
              className="flex-1"
              data-testid="doc-view-again-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Document
            </Button>
            <Button
              onClick={handleConfirmSign}
              disabled={!agreed || !legalName.trim() || !!nameError || loading}
              className="flex-1 bg-green-500 hover:bg-green-600"
              data-testid="doc-confirm-sign-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />I Agree & Sign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Main view: Document Checklist
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        data-testid="document-checklist-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Document Signing Checklist
              </DialogTitle>
              <DialogDescription>
                Complete all documents to proceed with closing
              </DialogDescription>
            </div>
            {allComplete ? (
              <div className="flex items-center gap-2 text-green-500 bg-green-500/10 px-3 py-1 rounded-full">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">Complete</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-orange-500 bg-orange-500/10 px-3 py-1 rounded-full">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">In Progress</span>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Deal Info */}
        <div className="bg-muted/50 rounded-lg p-4 mb-4">
          <p className="font-medium text-foreground">{deal.property_address}</p>
          <p className="text-sm text-muted-foreground">
            {deal.city}, {deal.state} {deal.zip_code}
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Progress</span>
            <span className="text-sm font-medium">
              {signedCount} of {dealDocuments.length} signed
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Document List */}
        <div className="space-y-3">
          {dealDocuments.map((doc, index) => {
            const docItem = {
              key: doc.name,
              label: doc.label,
              description: doc.description || doc.label,
              content: doc.content || "",
            };
            const isSigned = documentsState[docItem.key] !== null;

            return (
              <div
                key={docItem.key}
                className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                  isSigned
                    ? "bg-green-500/5 border-green-500/20"
                    : "bg-card border-border hover:border-blue-500/50"
                }`}
                data-testid={`document-item-${docItem.key}`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isSigned ? "bg-green-500" : "bg-muted"
                    }`}
                  >
                    {isSigned ? (
                      <Check className="w-4 h-4 text-white" />
                    ) : (
                      <span className="text-sm font-medium text-muted-foreground">
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <div>
                    <p
                      className={`font-medium ${isSigned ? "text-green-600 dark:text-green-400" : "text-foreground"}`}
                    >
                      {docItem.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {docItem.description}
                    </p>
                    {isSigned && documentsState[docItem.key] && (
                      <p className="text-xs text-green-500 mt-1">
                        Signed on{" "}
                        {new Date(
                          typeof documentsState[docItem.key] === "string"
                            ? documentsState[docItem.key]
                            : documentsState[docItem.key]?.signed_at ||
                                documentsState[docItem.key],
                        ).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDocument(docItem.key, deal.deal_id)}
                    disabled={viewingDoc === docItem.key}
                    data-testid={`view-${docItem.key}-btn`}
                  >
                    {viewingDoc === docItem.key ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                    View
                  </Button>
                  {!isSigned && (
                    <Button
                      size="sm"
                      onClick={() => handleStartSign(docItem)}
                      className="bg-blue-500 hover:bg-blue-600"
                      data-testid={`sign-${docItem.key}-btn`}
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      Sign
                    </Button>
                  )}
                  {isSigned && (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Status Message */}
        {allComplete && (
          <div className="mt-6 bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-600 dark:text-green-400">
                  All Documents Complete!
                </p>
                <p className="text-sm text-green-600/80 dark:text-green-400/80 mt-1">
                  Your deal is now in the closing process. You will be notified
                  of next steps.
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentChecklistModal;
